import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  Container,
  Paper,
  Divider,
  CircularProgress
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { useThemeMode } from '../../contexts/ThemeContext';
import {
  startAuthentication,
  browserSupportsWebAuthn
} from '@simplewebauthn/browser';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const Login = () => {
  const { mode } = useThemeMode();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [mfaPending, setMfaPending] = useState(null);
  const [mfaMethod, setMfaMethod] = useState(null);
  const [otpCode, setOtpCode] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpSending, setOtpSending] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const [pendingNotice, setPendingNotice] = useState('');
  const { login, completePasskeyLogin, completeEmailOtpLogin, completeTotpLogin } = useAuth();

  const handleVerifyTotp = async () => {
    setError('');
    setLoading(true);
    try {
      const resp = await fetch(`${API_BASE_URL}/api/auth/totp/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${mfaPending.preAuthToken}` },
        body: JSON.stringify({ token: totpCode.trim() })
      });
      const data = await resp.json();
      if (data.success && data.token) {
        completeTotpLogin(data.token, data.user);
      } else {
        setError(data.error || 'Invalid code');
      }
    } catch (err) {
      setError(err.message || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSendOtp = async () => {
    setError('');
    setOtpSending(true);
    try {
      const resp = await fetch(`${API_BASE_URL}/api/auth/email-otp/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${mfaPending.preAuthToken}` },
        body: JSON.stringify({ email: mfaPending.user.email })
      });
      const data = await resp.json();
      if (data.success) {
        setOtpSent(true);
      } else {
        setError(data.error || 'Failed to send code');
      }
    } catch (err) {
      setError(err.message || 'Failed to send code');
    } finally {
      setOtpSending(false);
    }
  };

  const handleVerifyOtp = async () => {
    setError('');
    setLoading(true);
    try {
      const resp = await fetch(`${API_BASE_URL}/api/auth/email-otp/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${mfaPending.preAuthToken}` },
        body: JSON.stringify({ token: otpCode.trim() })
      });
      const data = await resp.json();
      if (data.success && data.token) {
        completeEmailOtpLogin(data.token, data.user);
      } else {
        setError(data.error || 'Invalid code');
      }
    } catch (err) {
      setError(err.message || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handlePasskeyVerify = async (preAuthToken, userObj) => {
    setError('');
    setLoading(true);
    try {
      const optResp = await fetch(`${API_BASE_URL}/api/auth/webauthn/login-options`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: userObj.username })
      });
      const optData = await optResp.json();
      if (!optData.success) {
        setError(optData.error || 'Failed to start passkey authentication');
        setMfaPending(null);
        return;
      }

      const asseResp = await startAuthentication({ optionsJSON: optData.options });

      const verifyResp = await fetch(`${API_BASE_URL}/api/auth/webauthn/login-verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ response: asseResp, challengeKey: optData.challengeKey })
      });
      const verifyData = await verifyResp.json();

      if (verifyData.success && verifyData.token) {
        completePasskeyLogin(verifyData.token, verifyData.user);
      } else {
        setError(verifyData.error || 'Passkey verification failed');
        setMfaPending(null);
      }
    } catch (err) {
      setError(err.message || 'Passkey authentication failed');
      setMfaPending(null);
    } finally {
      setLoading(false);
    }
  };

  const [verifyPending, setVerifyPending] = useState(null); // { signupToken, user }
  const [signupCode, setSignupCode] = useState('');
  const [signupCodeSent, setSignupCodeSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(username, password);
      if (!result.success) {
        if (result.pendingApproval) {
          setPendingNotice(result.error || 'Your account is pending administrator approval.');
        } else {
          setError(result.error);
        }
      } else if (result.emailVerificationRequired) {
        setVerifyPending({ signupToken: result.signupToken, user: result.user });
      } else if (result.mfaRequired) {
        setMfaPending(result);
        setMfaMethod(result.mfaRequired);
      }
    } catch (err) {
      setError('An error occurred during login');
    } finally {
      setLoading(false);
    }
  };

  const handleSendSignupCode = async () => {
    setError('');
    setOtpSending(true);
    try {
      const resp = await fetch(`${API_BASE_URL}/api/auth/signup/email-code/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${verifyPending.signupToken}` }
      });
      const data = await resp.json();
      if (data.success) setSignupCodeSent(true);
      else setError(data.error || 'Failed to send code');
    } catch (err) {
      setError(err.message || 'Failed to send code');
    } finally {
      setOtpSending(false);
    }
  };

  const handleVerifySignupCode = async () => {
    setError('');
    setLoading(true);
    try {
      const resp = await fetch(`${API_BASE_URL}/api/auth/signup/email-code/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${verifyPending.signupToken}` },
        body: JSON.stringify({ token: signupCode.trim() })
      });
      const data = await resp.json();
      if (data.success) {
        setVerifyPending(null);
        setError('');
        setPendingNotice('Email verified. Your account is now pending administrator approval — you will be able to sign in once approved.');
      } else {
        setError(data.error || 'Invalid code');
      }
    } catch (err) {
      setError(err.message || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordlessPasskey = async () => {
    setError('');
    setLoading(true);
    try {
      const optResp = await fetch(`${API_BASE_URL}/api/auth/webauthn/login-options`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      const optData = await optResp.json();
      if (!optData.success) {
        setError(optData.error || 'Failed to start passkey authentication');
        return;
      }

      const asseResp = await startAuthentication({ optionsJSON: optData.options });

      const verifyResp = await fetch(`${API_BASE_URL}/api/auth/webauthn/login-verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ response: asseResp, challengeKey: optData.challengeKey })
      });
      const verifyData = await verifyResp.json();

      if (verifyData.success && verifyData.token) {
        completePasskeyLogin(verifyData.token, verifyData.user);
      } else {
        setError(verifyData.error || 'Passkey verification failed');
      }
    } catch (err) {
      setError(err.message || 'Passkey authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const switchMfaMethod = (method) => {
    setMfaMethod(method);
    setError('');
    setOtpCode('');
    setOtpSent(false);
    setTotpCode('');
  };

  const webauthnSupported = browserSupportsWebAuthn();

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Paper elevation={3} sx={{ width: '100%', maxWidth: 400 }}>
          <Card>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Box
                  component="img"
                  src={mode === 'dark' ? '/assets/mytradeshub-logo-dark.png' : '/assets/mytradeshub-logo.png'}
                  alt="myTradesHub"
                  sx={{ height: 110, maxWidth: '100%', objectFit: 'contain', mb: 1 }}
                />
                <Typography variant="body1" color="text.secondary">
                  Please sign in to continue
                </Typography>
              </Box>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              {pendingNotice && (
                <Alert severity="info" sx={{ mb: 2 }} onClose={() => setPendingNotice('')}>
                  {pendingNotice}
                </Alert>
              )}

              {verifyPending ? (
                <Box>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Verify your email to finish setting up your account. A code will be sent to {verifyPending.user.email}.
                  </Alert>
                  {!signupCodeSent ? (
                    <Button
                      fullWidth
                      variant="contained"
                      sx={{ mb: 2 }}
                      disabled={otpSending}
                      onClick={handleSendSignupCode}
                    >
                      {otpSending ? <CircularProgress size={20} /> : 'Send Verification Code'}
                    </Button>
                  ) : (
                    <Box>
                      <TextField
                        fullWidth
                        label="Verification Code"
                        variant="outlined"
                        value={signupCode}
                        onChange={(e) => setSignupCode(e.target.value.replace(/\D/g, ''))}
                        margin="normal"
                        autoFocus
                        inputProps={{ maxLength: 6, inputMode: 'numeric' }}
                      />
                      <Button
                        fullWidth
                        variant="contained"
                        sx={{ mt: 2, mb: 2 }}
                        disabled={loading || signupCode.length < 6}
                        onClick={handleVerifySignupCode}
                      >
                        {loading ? <CircularProgress size={20} /> : 'Verify Code'}
                      </Button>
                      <Button fullWidth variant="text" onClick={handleSendSignupCode} disabled={otpSending}>
                        Resend code
                      </Button>
                    </Box>
                  )}
                  <Button
                    fullWidth
                    variant="text"
                    sx={{ mt: 1 }}
                    onClick={() => { setVerifyPending(null); setSignupCode(''); setSignupCodeSent(false); setPassword(''); }}
                  >
                    Back to login
                  </Button>
                </Box>
              ) : mfaPending ? (
                <Box>
                  {mfaMethod === 'passkey' ? (
                    <>
                      <Alert severity="info" sx={{ mb: 2 }}>
                        Verify your identity with a passkey to continue.
                      </Alert>
                      <Button
                        fullWidth
                        variant="contained"
                        sx={{ mb: 2 }}
                        disabled={loading}
                        onClick={() => handlePasskeyVerify(mfaPending.preAuthToken, mfaPending.user)}
                      >
                        {loading ? <CircularProgress size={20} /> : 'Verify with Passkey'}
                      </Button>
                    </>
                  ) : mfaMethod === 'totp' ? (
                    <>
                      <Alert severity="info" sx={{ mb: 2 }}>
                        Enter the 6-digit code from your authenticator app (Google Authenticator, Authy, etc.).
                      </Alert>
                      <TextField
                        fullWidth
                        label="Authenticator Code"
                        variant="outlined"
                        value={totpCode}
                        onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
                        margin="normal"
                        autoFocus
                        inputProps={{ maxLength: 6, inputMode: 'numeric' }}
                      />
                      <Button
                        fullWidth
                        variant="contained"
                        sx={{ mt: 2, mb: 2 }}
                        disabled={loading || totpCode.length < 6}
                        onClick={handleVerifyTotp}
                      >
                        {loading ? <CircularProgress size={20} /> : 'Verify Code'}
                      </Button>
                    </>
                  ) : (
                    <>
                      <Alert severity="info" sx={{ mb: 2 }}>
                        A verification code was sent to {mfaPending.user.email}. Enter it below to continue.
                      </Alert>
                      {!otpSent ? (
                        <Button
                          fullWidth
                          variant="contained"
                          sx={{ mb: 2 }}
                          disabled={otpSending}
                          onClick={handleSendOtp}
                        >
                          {otpSending ? <CircularProgress size={20} /> : 'Send Code'}
                        </Button>
                      ) : (
                        <Box>
                          <TextField
                            fullWidth
                            label="Verification Code"
                            variant="outlined"
                            value={otpCode}
                            onChange={(e) => setOtpCode(e.target.value)}
                            margin="normal"
                            autoFocus
                            inputProps={{ maxLength: 6, inputMode: 'numeric' }}
                          />
                          <Button
                            fullWidth
                            variant="contained"
                            sx={{ mt: 2, mb: 2 }}
                            disabled={loading || otpCode.length < 6}
                            onClick={handleVerifyOtp}
                          >
                            {loading ? <CircularProgress size={20} /> : 'Verify Code'}
                          </Button>
                          <Button
                            fullWidth
                            variant="text"
                            onClick={handleSendOtp}
                            disabled={otpSending}
                          >
                            Resend code
                          </Button>
                        </Box>
                      )}
                    </>
                  )}

                  {mfaPending.mfaMethods && mfaPending.mfaMethods.length > 1 && (
                    <Box sx={{ mt: 2 }}>
                      <Divider sx={{ mb: 2 }}>or use</Divider>
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                        {mfaPending.mfaMethods.map((method) =>
                          method === mfaMethod ? null : (
                            <Button
                              key={method}
                              variant="outlined"
                              size="small"
                              onClick={() => switchMfaMethod(method)}
                            >
                              {method === 'passkey' ? 'Use Passkey' : method === 'totp' ? 'Use Authenticator App' : 'Use Email Code'}
                            </Button>
                          )
                        )}
                      </Box>
                    </Box>
                  )}

                  <Button
                    fullWidth
                    variant="text"
                    sx={{ mt: 2 }}
                    onClick={() => { setMfaPending(null); setMfaMethod(null); setPassword(''); setOtpCode(''); setOtpSent(false); setTotpCode(''); }}
                  >
                    Back to login
                  </Button>
                </Box>
              ) : (
                <>
                  <Box component="form" onSubmit={handleSubmit}>
                    <TextField
                      fullWidth
                      label="Username"
                      variant="outlined"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      margin="normal"
                      required
                      autoFocus
                    />
                    <TextField
                      fullWidth
                      label="Password"
                      type="password"
                      variant="outlined"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      margin="normal"
                      required
                    />
                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      sx={{ mt: 3, mb: 2 }}
                      disabled={loading}
                    >
                      {loading ? 'Signing In...' : 'Sign In'}
                    </Button>
                  </Box>

                  {webauthnSupported && (
                    <>
                      <Divider sx={{ my: 2 }}>or</Divider>
                      <Button
                        fullWidth
                        variant="outlined"
                        disabled={loading}
                        onClick={handlePasswordlessPasskey}
                      >
                        Sign in with Passkey
                      </Button>
                    </>
                  )}

                  <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Enter your credentials to access the application
                    </Typography>
                    <Typography variant="body2" color="primary" sx={{ mb: 0.5 }}>
                      <a href="/forgot-password" style={{ textDecoration: 'none', color: 'inherit' }}>Forgot your password?</a>
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      New here? <a href="/signup" style={{ textDecoration: 'none', color: '#1976d2' }}>Create an account</a>
                    </Typography>
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Paper>
      </Box>
    </Container>
  );
};

export default Login;